/**
 * Microsoft Clarity wrapper.
 *
 * - Only enabled when VITE_CLARITY_ID is set at build time. Staging and
 *   local dev builds leave it unset, so the snippet never runs there.
 * - /admin routes are excluded entirely. The script is paused on entry
 *   to /admin and resumed on exit, so admin actions and any sensitive
 *   data on internal screens never reach Clarity.
 * - identifyClarityUser links the recorded session to the SiteForge
 *   user id once auth resolves, so user-level filtering + funnel
 *   analysis works in the Clarity dashboard.
 */

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

const CLARITY_ID = (import.meta.env.VITE_CLARITY_ID || '').trim();
const ADMIN_PREFIX = '/admin';

let scriptInjected = false;
let lastIdentifiedUser: string | null = null;

function loadSnippet(): void {
  if (!CLARITY_ID || scriptInjected) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  scriptInjected = true;
  // Microsoft's official snippet, verbatim. Defines window.clarity as a
  // queueing stub before the remote tag.js loads, then injects the tag.
  /* eslint-disable */
  (function (c: any, l: any, a: string, r: string, i: string) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    const t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
    const y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', CLARITY_ID);
  /* eslint-enable */
}

function isAdminPath(pathname: string): boolean {
  return pathname === ADMIN_PREFIX || pathname.startsWith(ADMIN_PREFIX + '/');
}

/**
 * Call on every route change. Loads + resumes Clarity on non-admin
 * routes, pauses it on /admin. Safe to call repeatedly.
 */
export function applyClarityRoute(pathname: string): void {
  if (!CLARITY_ID) return;
  if (isAdminPath(pathname)) {
    if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
      window.clarity('stop');
    }
    return;
  }
  loadSnippet();
  if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
    // start is a no-op if already running; matters when navigating back
    // out of /admin where we explicitly stopped recording.
    window.clarity('start');
  }
}

/**
 * Link the active session to a SiteForge user. Called whenever the auth
 * state changes (login, refresh, impersonate). De-dupes against the last
 * identified user to avoid spamming the Clarity API.
 */
export function identifyClarityUser(userId: string | null | undefined, friendly?: string): void {
  if (!CLARITY_ID) return;
  if (typeof window === 'undefined' || typeof window.clarity !== 'function') return;
  if (!userId) {
    lastIdentifiedUser = null;
    return;
  }
  if (userId === lastIdentifiedUser) return;
  // Clarity.identify(userId, sessionId?, pageId?, friendlyName?)
  window.clarity('identify', userId, undefined, undefined, friendly || userId);
  lastIdentifiedUser = userId;
}
